import { useQueryClient } from '@tanstack/react-query'
import { Button, DatePicker, Flex, Form, Input, Modal, ModalProps } from 'antd'
import dayjs from 'dayjs'

import {
  useTreatmentServiceCancelTreatmentPlanEndpoint,
  useTreatmentServiceGetTreatmentPlanEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  ClassifierCategory,
  ClassifierType,
  GetTreatmentPlanResponse
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  ModalFooterProps,
  ClassifierInfiniteSelect,
  SaveConfirm
} from 'src/components'
import { useClassifierId } from 'src/hooks'
import { dayMonthYearBackSlash, yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  treatment?: GetTreatmentPlanResponse
  onCancel: () => void
} & ModalProps

const Footer = ({ onCancel, onSubmit }: ModalFooterProps) => {
  const { t } = useLocales()

  return (
    <Flex align="center" justify="center" gap={8}>
      <Button onClick={onCancel}>{t('users.cancel')}</Button>

      <SaveConfirm
        title={`${t('stock_applications.confirm')}?`}
        onConfirm={onSubmit}
      >
        <Button type="primary" danger>
          {t('stock_applications.confirm')}
        </Button>
      </SaveConfirm>
    </Flex>
  )
}

export const CancelModal = ({ treatment, onCancel }: Props) => {
  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const { t } = useLocales()

  const acuseId = useClassifierId(
    ClassifierType.ACCUSE,
    ClassifierCategory.TREATMENT
  )

  const { mutate: cancelTreatment } =
    useTreatmentServiceCancelTreatmentPlanEndpoint({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            useTreatmentServiceGetTreatmentPlanEndpointKey,
            treatment?.id
          ]
        })
        onCancel()
      },

      onError: (e: ApiError) => formErrorHandler(e.body.errors)
    })

  const onSubmit = async () => {
    const values = await form.validateFields()

    cancelTreatment({
      id: String(treatment?.id),
      requestBody: {
        cancellationDate: dayjs(values?.cancellationDate).format(
          yearMonthDaySlash
        ),
        cancellationReason: values?.cancellationReason,
        cancellationAccuseIds: values?.cancellationAccuseIds
      }
    })
  }

  return (
    <Modal
      open
      maskClosable={false}
      footer={Footer({
        onCancel,
        onSubmit: () => {
          void onSubmit()
        }
      })}
      title={t('treatments.cancel_treatment_title')}
      onCancel={onCancel}
    >
      <Form layout="vertical" form={form} requiredMark={false}>
        <Flex gap={24}>
          <Flex vertical gap={24} className="full-width">
            <Form.Item
              name="cancellationDate"
              label={t('treatments.event_date')}
              rules={requiredRules}
            >
              <DatePicker
                allowClear={false}
                className="full-width"
                format={dayMonthYearBackSlash}
                placeholder={t('analysis.add_test.test_date')}
                disabledDate={(current) =>
                  current && current < dayjs().startOf('day')
                }
              />
            </Form.Item>

            <Form.Item
              name="cancellationAccuseIds"
              label={t('consultations.accuse')}
              rules={requiredRules}
            >
              <ClassifierInfiniteSelect mode="multiple" id={acuseId} />
            </Form.Item>

            <Form.Item
              name="cancellationReason"
              label={t('treatments.cancel_reason')}
              rules={requiredRules}
            >
              <Input.TextArea rows={5} />
            </Form.Item>
          </Flex>
        </Flex>
      </Form>
    </Modal>
  )
}
